import { InlineIcon } from "@iconify/react";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { createUseStyles } from "react-jss";
import { components } from "react-select";
import {
  accommodationAutocomplete,
  fastDestAutoComplete,
  updateHotelScore,
} from "../../../../api";
import { cStyler } from "../../../../helpers";
import { StyleTypeContext } from "../../../../umbilical_tools";
import AsyncSelect from "react-select/async";
import { Field } from "formik";
import { autoCompleteInputStyles } from "..";
import jssVariables from "../../../../jss_setup";
import { useSelector } from "react-redux";

// ========================= SEARCH FUNCTIONS ========================
async function destSearch({ query, places_blacklist = [] }) {
  var data = await fastDestAutoComplete({
    name: _.trim(query.toLowerCase()),
  });

  const blacklistedCountryNames = places_blacklist
    .filter((i) => i.country_code)
    .map((i) => i?.meta?.name_en)
    .filter((i) => i);

  const blacklistedDestCodes = places_blacklist
    .filter((i) => i.destination_code)
    .map((i) => i.destination_code)
    .filter((i) => i);

  const dests = (data?.destinations ?? [])
    .filter((d) =>
      blacklistedCountryNames.length > 0
        ? blacklistedCountryNames.every(
            (n) => !d.destination.toLowerCase().includes(n.toLowerCase())
          )
        : true
    )
    .filter((d) =>
      blacklistedDestCodes.length > 0
        ? !blacklistedDestCodes.includes(`${d.type}__${d.id}`)
        : true
    )
    .filter((d) => d.score > 0.1)
    .sort((a, b) => b.score - a.score)
    .filter((___, idx) => idx < 20)
    .map((dest) => {
      const label = [...new Set(dest.destination.split("-"))].join(" - ");
      return {
        ...dest,
        label,
        value: label,
        location_type: "destination",
      };
    });

  return dests;
}

async function hotelSearch({ name, places_blacklist = [] }) {
  const blacklistedCountryCodes = new Set(
    places_blacklist.map((i) => i.country_code)
  );

  var data = await accommodationAutocomplete({ name: _.trim(name) });
  const hotels = (data?.candidates ?? [])
    .filter((c) => !blacklistedCountryCodes.has(c.country_code))
    .map((htl) => {
      const label = `${htl.name} - ${htl.address} - ${htl.country_name}`;
      return { ...htl, label, value: label, location_type: "hotel" };
    });
  return hotels.filter((i, idx) => idx < 9);
}

function promiseOptions(query, types, places_blacklist) {
  return new Promise((resolve) => {
    const promises = [destSearch({ query, places_blacklist })];
    if (types.includes("ACC")) {
      promises.push(hotelSearch({ name: query, places_blacklist }));
    }

    resolve(
      Promise.all(promises).then(([dests, hotels]) => {
        return [...(dests || []), ...(hotels || [])];
      })
    );
  });
}

const debouncedSearch = _.debounce(
  (query, types, places_blacklist, callback) => {
    if (query.length < 3) {
      callback([]);
      return;
    }

    promiseOptions(query, types, places_blacklist).then((result) =>
      callback(result)
    );
  },
  300
);

// =============== OPTIONS AND INPUT CONTROL COMPONENTS ==============

const optionItemStyles = createUseStyles({
  option: (props) => ({
    display: "inline-grid",
    gridTemplateColumns: "max-content 1fr",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.option", {}),
  }),
  icon: (props) => ({
    width: "2rem",
    ..._.get(props, "customStyles.icon", {}),
  }),
});
const OptionItem = ({ label, data, ...props }) => {
  const location_type = _.get(data, "location_type", "");
  const classes = optionItemStyles();
  return (
    <components.Option {...props}>
      <span className={classes.option}>
        <span className={classes.iconContainer}>
          {location_type === "hotel" ? (
            <InlineIcon icon="fa:bed" className={classes.icon} />
          ) : location_type === "destination" ? (
            <InlineIcon icon="fa:map-marker" className={classes.icon} />
          ) : (
            ""
          )}
        </span>
        <span className={classes.displayLabel}>{label}</span>
      </span>
    </components.Option>
  );
};

const inputControlStyles = createUseStyles({
  InputControl: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    width: "70vw",
    alignItems: "center",
    color: jssVariables.colors.text.dark,
    paddingLeft: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.InputControl", {}),
  }),
  iconContainer: (props) => ({
    padding: 0,
    ..._.get(props, "customStyles.iconContainer", {}),
  }),
  displayLabel: (props) => ({
    width: "100%",
    ..._.get(props, "customStyles.displayLabel", {}),
  }),
  icon: (props) => ({
    width: `calc(${jssVariables.normal_gap} * 2)`,
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    InputControl: (props) => ({
      width: "100%",
      ..._.get(props, "customStyles.InputControl.smallscreen", {}),
    }),
  },
});
const InputControl = ({ children, ...props }) => {
  const location_type = _.get(props, "selectProps.value.location_type", "");

  const { type, brand } = useContext(StyleTypeContext);

  const customStyles = cStyler({
    type,
    brand,
    objName: "AutocompleteControlStyles",
  });

  const classes = inputControlStyles({ customStyles });

  return (
    <components.Control {...props}>
      <span className={classes.InputControl}>
        <span className={classes.iconContainer}>
          {location_type === "hotel" ? (
            <InlineIcon icon="fa:bed" className={classes.icon} />
          ) : location_type === "destination" ? (
            <InlineIcon icon="fa:map-marker" className={classes.icon} />
          ) : (
            ""
          )}
        </span>
        <span className={classes.displayLabel}>{children}</span>
      </span>
    </components.Control>
  );
};

// ============= MAIN REACT SELECT AUTOCOMPLETE COMPONENT ============

const selectorStylesMobile = (props) => {
  return {
    placeholder: (base) => ({
      ...base,
      color: jssVariables.colors.text.dark,
      fontSize: "medium",
      ..._.get(props, "customStyles.placeholder", {}),
    }),
    singleValue: (base) => ({
      ...base,
      color: jssVariables.colors.text.dark,
      fontSize: "medium",
      ..._.get(props, "customStyles.singleValue", {}),
    }),
    control: (base) => ({
      ...base,
      border: `1px solid ${jssVariables.colors.border}`,
      height: "3rem",
      ..._.get(props, "customStyles.control", {}),
    }),
    input: (base) => ({
      ...base,
      color: jssVariables.colors.text.dark,
      height: `calc(${jssVariables.normal_gap} * 3.2)`,
      alignContent: "center",
      ..._.get(props, "customStyles.input", {}),
    }),
    indicatorsContainer: () => ({
      display: "none",
      ..._.get(props, "customStyles.indicatorsContainer", {}),
    }),
    valueContainer: (base) => ({
      ...base,
      justifyItems: "start",
      paddingLeft: 0,
      ..._.get(props, "customStyles.valueContainer", {}),
    }),
    option: (base) => ({
      ...base,
      width: "98%",
      color: jssVariables.colors.text.dark,
      ..._.get(props, "customStyles.option", {}),
    }),
  };
};
const DestAccAutocompleteRSelect = ({
  types,
  placeholder,
  onSelect,
  customStyles,
}) => {
  const places_blacklist = useSelector(
    (state) => state?.WebsiteMetadata?.metadata?.places_blacklist ?? []
  );

  function updateScore(item) {
    if (item) {
      if (item.location_type === "hotel") {
        updateHotelScore({ uid: item.uid });
      }
    }
  }

  var commonProps = {
    isClearable: true,
    defaultOptions: true,
    blurInputOnSelect: true,
    loadOptions: (query, callback) =>
      debouncedSearch(query, types, places_blacklist, callback),
    onChange: (item) => {
      if (!item) {
        return;
      }
      updateScore(item);
      onSelect({
        destination_code: [`${item.type}___${item.id}`],
        destination_meta: item,
      });
    },
    placeholder,
    components: {
      Option: OptionItem,
      Control: InputControl,
    },
  };

  return (
    <AsyncSelect
      {...commonProps}
      menuPlacement="top"
      styles={selectorStylesMobile({ customStyles })}
    />
  );
};
DestAccAutocompleteRSelect.defaultProps = {
  types: ["dest"],
  placeholder: "",
  customStyles: {},
};
DestAccAutocompleteRSelect.propTypes = {
  types: PropTypes.array.isRequired,
  placeholder: PropTypes.string.isRequired,
  customStyles: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const HotelDestAutoCompleteInputComponent = ({
  field,
  form: { touched, errors, setFieldValue },
  ...props
}) => {
  return (
    <React.Fragment>
      <DestAccAutocompleteRSelect
        types={props.types}
        onSelect={(dest) => setFieldValue(field.name, dest)}
        {...props}
      />
      {touched[field.name] && errors[field.name] && (
        <div className={props.styles.errorMessage}>{errors[field.name]}</div>
      )}
    </React.Fragment>
  );
};
HotelDestAutoCompleteInputComponent.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
};

export const HotelDestAutoCompleteInputField = ({
  label,
  customStyles,
  ...props
}) => {
  const classes = autoCompleteInputStyles({ customStyles });

  return (
    <div className={classes.inputGroup}>
      <label className={classes.label}>{label}</label>
      <Field
        styles={classes}
        {...props}
        customStyles={customStyles}
        component={HotelDestAutoCompleteInputComponent}
      />
    </div>
  );
};
HotelDestAutoCompleteInputField.defaultProps = {
  label: "",
  name: "",
  types: [],
  placeholder: "",
  customStyles: {},
  isInFilters: false,
};
HotelDestAutoCompleteInputField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  types: PropTypes.array,
  placeholder: PropTypes.string,
  customStyles: PropTypes.object,
  isInFilters: PropTypes.bool,
};
